import {
    ReactElement,
    useRef,
} from 'react';

import {Alert, Group, Stack,} from '@mantine/core';
import {AiOutlineWarning} from 'react-icons/ai';
import {I18n} from '@aws-amplify/core';

import {useCodePipelines} from '../hooks/useCodePipelines.ts';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {useCodePipelinesSelection} from '../hooks/useCodePipelinesSelection.ts';
import {useVerifiedPermissions} from "../../../../hooks/useVerifiedPermissions.ts";
import {useCodePipelinesSearch} from "../hooks/useCodePipelinesSearch.ts";
import {useCodePipelinesTableColumns} from "../hooks/useCodePipelinesTableColumns.tsx";
import {rbacActions} from "../../../../config/rbac/actions.ts";
import {DataTableColumn, DataTableSortStatus} from "mantine-datatable";
import {CodePipeline} from "../types.ts";

export const RecordsTable = ({
                                 extraTools,
                             }: {
    extraTools?: ReactElement[],
}) => {

    const {allowedActions} = useVerifiedPermissions()

    const {
        visibleItems: records,
        loading,
        isError,
        isFetchingNextPage,
        unfilteredCount,
        sortStatus,
        setSortStatus,
        nextToken,
        fetchNextPage,
    } = useCodePipelines();

    const {
        columns,
    } = useCodePipelinesTableColumns();

    const {
        keyword,
        handleFilterByKeyword,
    } = useCodePipelinesSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection
    } = useCodePipelinesSelection();

    let selectedRecords = [] as CodePipeline[];
    if (allowedActions.includes(rbacActions.DeleteCodePipeline)) {
        selectedRecords = records.filter(record => selection[record.name])
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Three distinct states, per the checklist: a failed load must not look
    // like an empty dataset, and neither must a search that matched nothing.
    if (isError) {
        return (
            <Alert
                color='red'
                icon={<AiOutlineWarning/>}
                title={I18n.get('Could not load code pipelines')}
            >
                {I18n.get('The request failed. Reload the page to try again.')}
            </Alert>
        );
    }

    const noRecordsText = keyword
        ? I18n.get('No code pipelines match your search')
        : I18n.get('No code pipelines found');

    return (
        <Stack>
            <Group justify='space-between'>
                <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>
                <Group>{extraTools}</Group>
            </Group>

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    (items as CodePipeline[]).forEach((item) => {
                        handleRowSelection(item.name, true)
                    })
                }}
                fetching={loading}
                noRecordsText={noRecordsText}
                sortStatus={sortStatus as DataTableSortStatus}
                onSortStatusChange={setSortStatus as (status: DataTableSortStatus) => void}
                idAccessor="name"
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={unfilteredCount}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='code pipeline'
            />
        </Stack>
    );
};
